import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { displayName } from '../../utils/userLabel';
import Dialog from '../ui/Dialog';
import PeoplePicker from './PeoplePicker';
import * as S from '../specimens/styles';

/*
 * Who is in a conversation (GET /chat/channels/{id}'s `members`). A group's
 * owner renames it, adds people and removes them; anyone may leave a group
 * (the server hands the ownership on when the owner leaves). A direct
 * conversation cannot be left -- it can be muted.
 */
export default function MembersDialog({ channel, me, onClose, onChanged, onLeft }) {
  const { t } = useTranslation(['chat', 'common']);
  const [adding, setAdding] = useState([]);
  const [title, setTitle] = useState(channel.title || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const manage = channel.can?.manage;

  const run = (call, after) => {
    setBusy(true);
    setError('');
    return call()
      .then((r) => after?.(r))
      .catch((e) => setError(errorText(e)))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog title={t('members')} onClose={onClose} testId="chat-members" width={460}>
      {manage && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
          <label style={{ ...S.label, flex: 1 }}>
            {t('groupName')}
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} style={S.input}
              data-testid="chat-rename" />
          </label>
          <button type="button" style={S.ghostBtn} disabled={busy || !title.trim() || title.trim() === channel.title}
            onClick={() => run(() => api.put(`/chat/channels/${channel.channel_id}`, { title: title.trim() }), onChanged)}>
            {t('rename')}
          </button>
        </div>
      )}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="chat-member-list">
        {(channel.members || []).map((m) => (
          <li key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
            borderBottom: '1px solid var(--c-border)' }}>
            <span style={{ flex: 1 }}>
              {displayName(m.name, t)} {m.role === 'owner' && <span style={S.hint}>· {t('owner')}</span>}
              {m.user_id === me && <span style={S.hint}> · {t('you')}</span>}
            </span>
            {manage && m.user_id !== me && (
              <button type="button" style={{ ...S.ghostBtn, padding: '2px 8px', fontSize: 13 }} disabled={busy}
                onClick={() => run(() => api.delete(`/chat/channels/${channel.channel_id}/members/${m.user_id}`), onChanged)}
                data-testid="chat-remove-member">{t('remove')}</button>
            )}
          </li>
        ))}
      </ul>
      {manage && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 15, margin: '0 0 8px' }}>{t('addPeople')}</h3>
          <PeoplePicker multiple chosen={adding} exclude={(channel.members || []).map((m) => m.user_id)}
            onPick={(p) => setAdding((a) => [...a, p])}
            onRemove={(p) => setAdding((a) => a.filter((x) => x.user_id !== p.user_id))} />
          <button type="button" style={{ ...S.btn(), marginTop: 8 }} disabled={busy || adding.length === 0}
            data-testid="chat-add-members"
            onClick={() => run(() => api.post(`/chat/channels/${channel.channel_id}/members`,
              { user_ids: adding.map((p) => p.user_id) }), () => { setAdding([]); onChanged?.(); })}>
            {t('add')}
          </button>
        </div>
      )}
      {error && <p role="alert" style={S.error}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'space-between' }}>
        {channel.can?.leave ? (
          <button type="button" style={{ ...S.ghostBtn, color: 'var(--c-danger-fg)' }} disabled={busy}
            data-testid="chat-leave"
            onClick={() => run(() => api.delete(`/chat/channels/${channel.channel_id}/members/${me}`), onLeft)}>
            {t('leave')}
          </button>
        ) : <span />}
        <button type="button" style={S.ghostBtn} onClick={onClose}>{t('close')}</button>
      </div>
    </Dialog>
  );
}
